'use client'

import { useMemo, useState } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import { useFilterStore } from '@/stores/filterStore'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts'
import { isoWeek, isoMonth, isoQuarter } from '@/lib/dates'
import Badge from '@/components/ui/Badge'

export default function TeamPage() {
  const { isLoading } = useDashboardData()
  const { periodTS, kpis, filterParams } = useFilteredData()
  const { engineer } = useFilterStore()
  const [meetingGroup, setMeetingGroup] = useState<'week' | 'month' | 'quarter'>('week')

  // Hours by engineer
  const engHoursData = useMemo(() => {
    const byEng: Record<string, { total: number; mso: number; pso: number; internal: number }> = {}
    periodTS.forEach(ts => {
      if (!byEng[ts.user]) byEng[ts.user] = { total: 0, mso: 0, pso: 0, internal: 0 }
      byEng[ts.user].total += ts.hours
      if (ts.cat === 'MSO') byEng[ts.user].mso += ts.hours
      else if (ts.cat === 'PSO') byEng[ts.user].pso += ts.hours
      else byEng[ts.user].internal += ts.hours
    })
    return Object.entries(byEng)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([name, d]) => ({ name: name.split(' ')[0], fullName: name, ...d }))
  }, [periodTS])

  // Billable data
  const billableData = useMemo(() => [
    { name: 'MSO', hours: kpis.msoH, fill: '#2563eb' },
    { name: 'PSO', hours: kpis.psoH, fill: '#60a5fa' },
    { name: 'Internal', hours: kpis.intH, fill: '#94a3b8' },
  ], [kpis])

  // Meetings vs Maker per engineer
  const meetingsData = useMemo(() => {
    const byEng: Record<string, { meeting: number; maker: number; total: number }> = {}
    periodTS.forEach(ts => {
      if (!byEng[ts.user]) byEng[ts.user] = { meeting: 0, maker: 0, total: 0 }
      byEng[ts.user].total += ts.hours
      if (ts.is_meeting) byEng[ts.user].meeting += ts.hours
      else byEng[ts.user].maker += ts.hours
    })
    return Object.entries(byEng).sort((a, b) => b[1].total - a[1].total)
      .map(([name, d]) => ({ name, meetPct: d.total > 0 ? (d.meeting / d.total * 100) : 0, ...d }))
  }, [periodTS])

  // Meeting trend
  const meetingTrend = useMemo(() => {
    const groupFn = meetingGroup === 'week' ? isoWeek : meetingGroup === 'month' ? isoMonth : isoQuarter
    const byPeriod: Record<string, { meeting: number; maker: number }> = {}
    periodTS.forEach(ts => {
      const k = groupFn(ts.date)
      if (!byPeriod[k]) byPeriod[k] = { meeting: 0, maker: 0 }
      if (ts.is_meeting) byPeriod[k].meeting += ts.hours
      else byPeriod[k].maker += ts.hours
    })
    return Object.entries(byPeriod).sort(([a], [b]) => a.localeCompare(b)).slice(-12)
      .map(([key, d]) => ({
        label: key.includes('W') ? key.slice(5) : key,
        meeting: Math.round(d.meeting),
        maker: Math.round(d.maker),
      }))
  }, [periodTS, meetingGroup])

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  const isIndividual = engineer !== 'all'

  return (
    <div className="space-y-5">
      {/* Hours by Engineer */}
      {!isIndividual && (
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <h3 className="text-base font-semibold mb-1">Hours by Engineer</h3>
          <p className="text-sm text-slate-400 mb-4">Split by MSO / PSO / Internal.</p>
          <ResponsiveContainer width="100%" height={Math.max(200, engHoursData.length * 32)}>
            <BarChart data={engHoursData} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} stroke="#cbd5e1" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} stroke="#cbd5e1" width={65} />
              <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 0, fontSize: 13, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }} formatter={(v) => `${Number(v).toFixed(1)}h`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="mso" stackId="a" fill="#2563eb" name="MSO" />
              <Bar dataKey="pso" stackId="a" fill="#60a5fa" name="PSO" />
              <Bar dataKey="internal" stackId="a" fill="#94a3b8" name="Internal" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* Billable Utilization */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">Billable Utilization</h3>
        <p className="text-sm text-slate-400 mb-4">
          Client hours (MSO+PSO) vs internal. Billable: <span className={kpis.billPct > 60 ? 'text-emerald-700 dark:text-emerald-400' : kpis.billPct > 40 ? 'text-amber-700 dark:text-amber-400' : 'text-red-700 dark:text-red-400'}>{kpis.billPct.toFixed(0)}%</span> ({(kpis.msoH + kpis.psoH).toFixed(0)}h of {kpis.totalH.toFixed(0)}h)
        </p>
        {/* Stacked bar */}
        <div className="flex h-8 overflow-hidden mb-4">
          {billableData.filter(d => d.hours > 0).map(d => (
            <div key={d.name} style={{ width: `${(d.hours / kpis.totalH) * 100}%`, background: d.fill }} className="flex items-center justify-center text-white text-xs font-semibold min-w-[30px]">
              {((d.hours / kpis.totalH) * 100).toFixed(0)}%
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {billableData.map(d => (
            <div key={d.name} className="flex items-center gap-3">
              <span className="text-sm w-16">{d.name}</span>
              <div className="flex-1 h-4 bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div style={{ width: `${kpis.totalH > 0 ? (d.hours / kpis.totalH) * 100 : 0}%`, background: d.fill }} className="h-full" />
              </div>
              <span className="text-sm font-semibold w-12 text-right">{d.hours.toFixed(0)}h</span>
            </div>
          ))}
        </div>
      </section>

      {/* Meetings vs Maker */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">
          Meetings vs Maker Time
          <span className="text-sm font-normal text-slate-400 ml-2">{kpis.meetH.toFixed(0)}h meetings / {kpis.totalH.toFixed(0)}h total</span>
        </h3>
        <p className="text-sm text-slate-400 mb-4">&gt;40% meeting ratio = too little deep work.</p>

        {/* Summary bar */}
        {kpis.totalH > 0 && (
          <div className="flex h-7 overflow-hidden mb-4">
            <div style={{ width: `${kpis.meetPct}%` }} className="bg-amber-600 flex items-center justify-center text-white text-xs font-semibold min-w-[40px]">
              Meetings {kpis.meetPct.toFixed(0)}%
            </div>
            <div style={{ width: `${100 - kpis.meetPct}%` }} className="bg-emerald-700 flex items-center justify-center text-white text-xs font-semibold min-w-[40px]">
              Maker {(100 - kpis.meetPct).toFixed(0)}%
            </div>
          </div>
        )}

        {/* Per engineer table */}
        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800">
              {['Engineer', 'Total', 'Meetings', 'Maker', 'Meeting %'].map(h => (
                <th key={h} className="text-left px-2 py-1.5 text-xs uppercase tracking-wider text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {meetingsData.map(d => (
              <tr key={d.name} className="border-b border-slate-100 dark:border-slate-800/50">
                <td className="px-2 py-1.5">{d.name}</td>
                <td className="px-2 py-1.5 text-slate-400">{d.total.toFixed(1)}h</td>
                <td className="px-2 py-1.5 text-slate-400">{d.meeting.toFixed(1)}h</td>
                <td className="px-2 py-1.5 text-slate-400">{d.maker.toFixed(1)}h</td>
                <td className="px-2 py-1.5">
                  <Badge variant={d.meetPct > 40 ? 'danger' : d.meetPct > 25 ? 'warning' : 'success'}>{d.meetPct.toFixed(1)}%</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Trend chart */}
        <div className="flex gap-1 mb-3">
          {(['week', 'month', 'quarter'] as const).map(g => (
            <button key={g} onClick={() => setMeetingGroup(g)} className={`px-2.5 py-1 text-sm ${meetingGroup === g ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
              {g === 'week' ? 'Weekly' : g === 'month' ? 'Monthly' : 'Quarterly'}
            </button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={meetingTrend}>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} stroke="#cbd5e1" />
            <YAxis tick={{ fontSize: 12, fill: '#64748b' }} stroke="#cbd5e1" width={30} />
            <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 0, fontSize: 13, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }} formatter={(v) => `${v}h`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="maker" stackId="a" fill="#059669" name="Maker" />
            <Bar dataKey="meeting" stackId="a" fill="#d97706" name="Meetings" radius={[1, 1, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>
    </div>
  )
}
